import React, { useContext, useState } from 'react';
import { StockStatus } from 'src/types/global-types';
import { DynamicExpandCollapseWrapper } from 'src/components/DynamicExpandCollapseWrapper/DynamicExpandCollapseWrapper';
import classnames from 'classnames';
import { SizeGuideLoader } from 'src/components/SizeGuide/SizeGuideLoader';
import { Button } from 'src/components/Button/Button';
import { ShippingGroups } from 'src/components/ProductDetail/ProductOptions';
import { MainContext } from 'src/components/Main/Main';

import { ProductSize } from './ProductSize';
import classes from './ProductSizes.scss';

export interface Size {
    title: string;
    id: string | number;
    stock?: StockStatus;
    maxQty: number | null;
    simpleProductId: number | null;
    simpleProductSku: string | null;
    hideStockSource?: boolean;
}
type Props = {
    title?: React.ReactNode;
    sizes: Size[];
    sizeGuide: string;
    isBundle?: boolean;
    fieldPrefix: string;
    afterList?: React.ReactNode;
    loading?: boolean;
    quantities?: Record<string, string>;
    sku?: string;
    color?: string;
    notify?: (size: Size) => void;
    selectCorrectShippingMethods?: (sku: string, value: number) => void;
    simpleProductsShippingGroups?: ShippingGroups;
    parentProductShippingGroup?: string;
};

const NEXT_DAY_DELIVERY_SHIPPING_GROUP = 'Next Day Delivery';

export const ProductSizes: React.FunctionComponent<Props> = ({
    isBundle,
    title,
    sizes,
    sizeGuide,
    loading,
    quantities,
    fieldPrefix,
    afterList,
    sku,
    color,
    notify,
    selectCorrectShippingMethods,
    simpleProductsShippingGroups,
    parentProductShippingGroup,
}) => {
    const [showAllSizes, setShowAllSizes] = useState(false);
    const { shippingGroupList } = useContext(MainContext);
    const availableSizes = sizes.filter((size) => size.maxQty && size.maxQty > 0);
    const computedSizes = isBundle ? availableSizes : sizes;

    const existingShippingGroupsLabels = simpleProductsShippingGroups?.map(
        (simpleGroup) =>
            shippingGroupList.find((shippingGroup) => shippingGroup.value === simpleGroup.shipperhq_shipping_group)
                ?.label,
    );
    const nextDayDeliveryIsPresent = existingShippingGroupsLabels?.includes(NEXT_DAY_DELIVERY_SHIPPING_GROUP);

    const sizesList = (
        <ul
            className={classnames({
                [classes.newProductSizesList]: !isBundle,
                [classes.bundleSizesList]: isBundle,
            })}
            data-loading={String(loading)}
            data-test-id="SizeList"
        >
            {computedSizes.map((size) => {
                const initialValue = quantities && quantities[`size:${size.id}`];
                const cssClasses = classnames({
                    [classes.sizesItemBundle]: isBundle,
                });
                const shippingGroupCode = simpleProductsShippingGroups?.find(
                    (group) => group.sku === size.simpleProductSku,
                )?.shipperhq_shipping_group;
                const shippingGroupLabel =
                    shippingGroupList.find((item) => item.value === shippingGroupCode)?.label ||
                    shippingGroupList.find((item) => item.value === parentProductShippingGroup)?.label;
                const isNextDayDelivery = Boolean(
                    shippingGroupCode && shippingGroupLabel === NEXT_DAY_DELIVERY_SHIPPING_GROUP,
                );

                return (
                    <ProductSize
                        key={size.simpleProductId || size.simpleProductSku || size.id}
                        field={`${fieldPrefix}:${size.id}`}
                        size={size}
                        notify={notify}
                        classNames={cssClasses}
                        initialValue={initialValue}
                        isBundle={isBundle}
                        sku={sku}
                        color={color}
                        isNewProductQtySelect
                        selectCorrectShippingMethods={selectCorrectShippingMethods}
                        isNextDayDelivery={isNextDayDelivery}
                    />
                );
            })}
        </ul>
    );

    return (
        <div
            className={classnames({
                [classes.newProductSizes]: !isBundle,
                [classes.bundleSizes]: isBundle,
            })}
        >
            <div className={classes.sizesHeader}>
                <div className={classes.newSizesTitleContainer}>
                    {title}
                    {isBundle && sizeGuide && <SizeGuideLoader sizeGuide={sizeGuide} isBundle={isBundle} />}
                </div>
                <div className={classes.sizeGuide}>
                    {sizeGuide && <SizeGuideLoader sizeGuide={sizeGuide} isBundle={isBundle} />}
                </div>
            </div>
            <div className={classes.sizeGuideTextContainer}>
                {nextDayDeliveryIsPresent && (
                    <span className={classes.sizeGuideText}>
                        <img src="../../../static/icons/thunderbolt-blue.svg" alt="" /> Next Working Day Delivery
                    </span>
                )}
            </div>

            {isBundle && (
                <DynamicExpandCollapseWrapper itemsAmountExpanded={sizes.length}>
                    {sizesList}
                </DynamicExpandCollapseWrapper>
            )}

            {!isBundle && (
                <div className={classes.productContainer}>
                    <div
                        className={classnames(classes.productSizesContainer, {
                            [classes.productSizesContainerFull]: showAllSizes,
                        })}
                    >
                        {sizesList}
                    </div>

                    {sizes.length > 12 && (
                        <Button
                            className={classnames(classes.button, classes.buttonDesktop)}
                            onClick={() => setShowAllSizes(!showAllSizes)}
                        >
                            {!showAllSizes ? 'Show More' : 'Show Less'}{' '}
                        </Button>
                    )}

                    {sizes.length > 9 && (
                        <Button
                            className={classnames(classes.button, classes.buttonMobile)}
                            onClick={() => setShowAllSizes(!showAllSizes)}
                        >
                            {!showAllSizes ? 'Show More' : 'Show Less'}{' '}
                        </Button>
                    )}
                </div>
            )}

            {afterList}
        </div>
    );
};
